import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  HeadingLevel, AlignmentType, WidthType, BorderStyle, PageOrientation,
  Header, Footer, PageNumber, ShadingType, ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import { fetchPhotos, photoReport, assetBytes } from "./photos";
import coverArt from "../../../HSE_Report_Cover.png";
import logoArt from "../../../momentum_logo.png";
import { fileNameFor } from "./exportPdf";

/**
 * The report as a Word document.
 *
 * The second consumer of the model. `ExportWordDoc.js` — 9,177 lines with 46
 * `reportType` checks — is not opened, imported from, or extended (design
 * §7a). This draws the same structure the PDF draws, from the same data, and
 * makes no decisions of its own.
 *
 * Word rather than PDF matters to clients who want to paste findings into
 * their own corrective-action tracker, which is most of them.
 */

const C = {
  ink: "1E2A28",
  slate: "5C6B68",
  tealDark: "245952",
  teal: "307268",
  gold: "EFC71D",
  sunk: "F1F4F3",
  white: "FFFFFF",
  yes: "2E7D4F",
  partial: "B8860B",
  no: "C0392B",
  na: "7C8A87",
};

const statusColour = (s) =>
  s === "Yes" ? C.yes : s === "Partial" ? C.partial : s === "No" ? C.no : C.na;

const FONT = "Montserrat";

/* ------------------------------------------------------------------ */
/* small builders                                                      */
/* ------------------------------------------------------------------ */

const text = (t, o = {}) =>
  new TextRun({ text: String(t == null ? "" : t), font: FONT, ...o });

const para = (t, o = {}) =>
  new Paragraph({
    children: Array.isArray(t) ? t : [text(t, o.run || {})],
    spacing: { after: o.after == null ? 120 : o.after, line: 276 },
    alignment: o.align,
    heading: o.heading,
    ...(o.extra || {}),
  });

const heading = (no, title) =>
  new Paragraph({
    children: [text(`${no}. ${title}`, { bold: true, size: 28, color: C.tealDark })],
    spacing: { before: 240, after: 60 },
    heading: HeadingLevel.HEADING_1,
    border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: C.gold, space: 4 } },
  });

const noBorders = {
  top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
  left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
  insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
};

const thinBorders = {
  top: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  bottom: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  left: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  right: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  insideVertical: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
};

const cell = (children, o = {}) =>
  new TableCell({
    children: Array.isArray(children) ? children : [children],
    width: o.width ? { size: o.width, type: WidthType.PERCENTAGE } : undefined,
    shading: o.fill ? { type: ShadingType.CLEAR, fill: o.fill } : undefined,
    margins: { top: 60, bottom: 60, left: 80, right: 80 },
    columnSpan: o.span,
  });

const headCell = (label, width) =>
  cell(
    new Paragraph({
      children: [text(label.toUpperCase(), { bold: true, size: 13, color: C.white })],
      spacing: { after: 0 },
    }),
    { width, fill: C.tealDark }
  );

const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (isNaN(x.getTime())) return String(d);
  return x.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const dateRange = (a, b) =>
  !a && !b ? "—" : a && b && a !== b ? `${fmtDate(a)} – ${fmtDate(b)}` : fmtDate(a || b);

const pctText = (n) => (n === null || n === undefined ? "Not assessed" : `${n}%`);

/** The line that tells two documents apart (§7b). Empty for a preview. */
function issueLine(header) {
  const i = header.issue;
  if (!i) return "";
  return [
    `Issue ${i.no}`,
    fmtDate(i.issued_at),
    `${i.assessed_count} assessed`,
    `${i.selected_count} reported`,
  ].join("  ·  ");
}

/* ------------------------------------------------------------------ */
/* sections                                                            */
/* ------------------------------------------------------------------ */

/*
 * A4 at 96 dpi, which is the unit docx measures an image in.
 * 8.27in x 11.69in -> 794 x 1123.
 */
const COVER_PX = { width: 794, height: 1123 };

/*
 * The wordmark in the running header.
 *
 * 120 wide is what the HSE and Electrical documents already use, so a client
 * who files all three sees the same mark at the same size. The height is the
 * artwork own 3.89:1 rather than HSE hardcoded 40, which stretched a
 * differently proportioned file.
 */
const HEADER_LOGO_PX = { width: 120, height: 31 };

function cover(model, art) {
  const h = model.header;
  const s = model.summary;

  const pair = (label, value) =>
    new TableRow({
      children: [
        cell(para([text(label.toUpperCase(), { bold: true, size: 14, color: C.slate })], { after: 20 }), { width: 34 }),
        cell(para([text(value, { size: 22 })], { after: 20 }), { width: 66 }),
      ],
    });

  /*
   * The artwork behind the words, so Word gets the cover the PDF has rather
   * than a typeset approximation of it. Floated behind the document because
   * an inline image would push the text onto page two.
   *
   * Without the bytes it falls back to the text cover this always had — a
   * report that cannot load a picture should still look deliberate.
   */
  const backdrop = art
    ? [new Paragraph({
        children: [new ImageRun({
          data: art,
          transformation: COVER_PX,
          floating: {
            horizontalPosition: { offset: 0 },
            verticalPosition: { offset: 0 },
            behindDocument: true,
          },
        })],
        spacing: { after: 0 },
      })]
    : [];

  return [
    ...backdrop,
    para([text("CRITICAL SAFETY ASSESSMENT", { bold: true, size: 18, color: C.teal })], { after: 200 }),
    para([text(h.organization, { bold: true, size: 52 })], { after: 60 }),
    para([text(h.site, { size: 28, color: C.slate })], { after: 400 }),

    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: noBorders,
      rows: [
        pair("Assessment dates", dateRange(h.start_date, h.end_date)),
        pair("Overall conformance",
          pctText(s.overall_pct) + (s.maturity_band ? ` — ${s.maturity_band}` : "")),
        pair("Prepared by", h.prepared_by || "—"),
        pair("Reviewed by", h.was_reviewed ? h.reviewed_by || "Reviewed" : "Not reviewed"),
        pair("Framework version", h.framework_version),
        pair("Report reference", h.report_id),
      ],
    }),
  ];
}

function narrative(section) {
  const paras = String(section.body || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return [
    heading(section.no, section.title),
    ...(paras.length
      ? paras.map((p) => para(p, { align: AlignmentType.JUSTIFIED }))
      : [para("This section has not been written.", { run: { italics: true, color: C.slate } })]),
  ];
}

function scoreAnalysis(section) {
  const d = section.data;

  const pillarRows = [
    new TableRow({
      children: [
        headCell("#", 8), headCell("Pillar", 46), headCell("Assessed", 14),
        headCell("Score", 14), headCell("Band", 18),
      ],
      tableHeader: true,
    }),
    ...d.pillars.map((p) =>
      new TableRow({
        children: [
          cell(para(String(p.no), { after: 0 })),
          cell(para(p.name, { after: 0 })),
          cell(para(`${p.assessed}/${p.total}`, { after: 0 })),
          cell(para([text(pctText(p.pct), { bold: true })], { after: 0 })),
          cell(para(p.band, { after: 0 })),
        ],
      })
    ),
  ];

  const subRows = [];
  d.pillars.forEach((p) => {
    subRows.push(
      new TableRow({
        children: [
          cell(para([text(`${p.no}. ${p.name}`, { bold: true, color: C.teal })], { after: 0 }),
            { fill: C.sunk, span: 4 }),
        ],
      })
    );
    p.sub_pillars.forEach((s) => {
      subRows.push(
        new TableRow({
          children: [
            cell(para(s.name, { after: 0 }), { width: 54 }),
            cell(para(`${s.assessed}/${s.total}`, { after: 0 }), { width: 14 }),
            cell(para([text(pctText(s.pct), { bold: true })], { after: 0 }), { width: 14 }),
            cell(para(s.band, { after: 0 }), { width: 18 }),
          ],
        })
      );
    });
  });

  return [
    heading(section.no, section.title),
    para([
      text(pctText(d.overall.pct), { bold: true, size: 44, color: C.tealDark }),
      text("   " + d.overall.band, { bold: true, size: 24 }),
    ]),
    para(
      `${d.overall.assessed} of ${d.overall.total} questions assessed` +
        (d.overall.not_assessed ? ` · ${d.overall.not_assessed} out of scope` : ""),
      { run: { color: C.slate, size: 18 }, after: 200 }
    ),

    para([text("By pillar", { bold: true, size: 22 })], { after: 80 }),
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: thinBorders, rows: pillarRows }),

    para([text("By sub-pillar", { bold: true, size: 22 })], { after: 80, extra: { spacing: { before: 300 } } }),
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: thinBorders, rows: subRows }),
  ];
}

function overallAssessment(section) {
  const d = section.data;
  return [
    heading(section.no, section.title),
    para([
      text(pctText(d.pct), { bold: true, size: 44, color: C.tealDark }),
      text("   " + d.band, { bold: true, size: 24 }),
    ]),
    para(d.statement, { align: AlignmentType.JUSTIFIED, after: 200 }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: thinBorders,
      rows: [
        new TableRow({
          children: [
            headCell("Conformant", 25), headCell("Partial", 25),
            headCell("Non-conformant", 25), headCell("Not applicable", 25),
          ],
          tableHeader: true,
        }),
        new TableRow({
          children: [
            cell(para(String(d.counts.conformant), { after: 0 })),
            cell(para(String(d.counts.partial), { after: 0 })),
            cell(para(String(d.counts.non_conformant), { after: 0 })),
            cell(para(String(d.counts.not_applicable), { after: 0 })),
          ],
        }),
      ],
    }),
  ];
}

function scope(section) {
  const d = section.data;
  const out = [
    heading(section.no, section.title),
    para(d.note, { align: AlignmentType.JUSTIFIED }),
    para(
      `${d.assessed} of ${d.total} questions were assessed` +
        (d.excluded ? `; ${d.excluded} were excluded.` : "."),
      { run: { color: C.slate, size: 18 }, after: 200 }
    ),
  ];

  d.exclusions.forEach((e) => {
    out.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: noBorders,
        rows: [
          new TableRow({
            children: [
              cell(
                [
                  para([text(e.name, { bold: true })], { after: 40 }),
                  para(e.reason, { run: { color: C.slate, size: 18 }, after: 0 }),
                ],
                { fill: "FFF8DC" }
              ),
            ],
          }),
        ],
      })
    );
    out.push(para("", { after: 80 }));
  });

  return out;
}

/**
 * Section 7 — the register.
 *
 * Its own landscape section, for the same reason the PDF switches orientation:
 * ten columns do not fit across a portrait page at a readable size.
 */
function findingsSection(section, header, images, assets = {}) {
  const d = section.data;
  const cols = d.columns;

  if (!d.rows.length) {
    return {
      properties: { page: { size: { orientation: PageOrientation.LANDSCAPE } } },
      headers: { default: wordHeader(header, assets.logo) },
      footers: { default: wordFooter(header) },
      children: [
        heading(section.no, section.title),
        para("No findings were selected for this report.", {
          run: { italics: true, color: C.slate },
        }),
      ],
    };
  }

  const rows = [
    new TableRow({
      children: cols.map((c) => headCell(c.label, c.width)),
      tableHeader: true, // repeats on every page the table spills onto
    }),
    ...d.rows.map((r) =>
      new TableRow({
        children: [
          cell(para(String(r.sr_no), { after: 0, run: { size: 15 } }), { width: cols[0].width }),
          cell(para(r.pillar, { after: 0, run: { size: 15 } }), { width: cols[1].width }),
          cell(para(r.sub_pillar, { after: 0, run: { size: 15 } }), { width: cols[2].width }),
          cell(para(r.verification_method || "—", { after: 0, run: { size: 15 } }), { width: cols[3].width }),
          cell(
            para([text(r.status, { bold: true, size: 15, color: C.white })], { after: 0 }),
            { width: cols[4].width, fill: statusColour(r.status) }
          ),
          cell(para(r.observation, { after: 0, run: { size: 15 } }), { width: cols[5].width }),
          cell(para(r.recommendation || "—", { after: 0, run: { size: 15 } }), { width: cols[6].width }),
          cell(para(r.legal_reference || "—", { after: 0, run: { size: 15 } }), { width: cols[7].width }),
          cell(
            para(r.score === null ? "—" : `${r.score}/${r.max_pts}`, { after: 0, run: { size: 15 } }),
            { width: cols[8].width }
          ),
          cell(
            new Paragraph({ children: evidenceRuns(r.photos, images), spacing: { after: 0 } }),
            { width: cols[9].width }
          ),
        ],
      })
    ),
  ];

  return {
    properties: { page: { size: { orientation: PageOrientation.LANDSCAPE } } },
    headers: { default: wordHeader(header, assets.logo) },
    footers: { default: wordFooter(header) },
    children: [
      heading(section.no, section.title),
      para(
        `${d.selected} finding${d.selected === 1 ? "" : "s"} shown, from ${d.assessed} questions assessed.`,
        { run: { color: C.slate, size: 18 }, after: 160 }
      ),
      new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: thinBorders, rows }),
    ],
  };
}

/**
 * Section 7, Elaborate — one entry per finding rather than a table row.
 *
 * Portrait, unlike the register: there is no ten-column table to fit, and the
 * prose that makes this layout worth choosing reads better in portrait.
 */
function elaborateFindings(section, header, images, assets = {}) {
  const d = section.data;

  const children = [
    heading(section.no, section.title),
    para(
      `${d.selected} finding${d.selected === 1 ? "" : "s"} shown, from ${d.assessed} questions assessed.`,
      { run: { color: C.slate, size: 18 }, after: 200 }
    ),
  ];

  if (!d.pillars.length) {
    children.push(
      para("No findings were selected for this report.", {
        run: { italics: true, color: C.slate },
      })
    );
  }

  d.pillars.forEach((p) => {
    children.push(
      para([text(p.name, { bold: true, size: 26, color: C.tealDark })], {
        after: 40, extra: { spacing: { before: 300 } },
      })
    );

    p.sub_pillars.forEach((s) => {
      children.push(
        new Paragraph({
          children: [text(s.name, { bold: true, size: 20, color: C.teal })],
          spacing: { before: 200, after: 100 },
          border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: "DCE3E1", space: 3 } },
        })
      );

      s.findings.forEach((f) => {
        children.push(
          para([
            text(f.number + "   ", { bold: true, size: 22 }),
            text(f.status_label, { bold: true, size: 18, color: statusColour(f.status) }),
          ], { after: 60 })
        );

        // The framework's own sentence — the finding itself.
        if (f.statement) {
          children.push(para(f.statement, { align: AlignmentType.JUSTIFIED, after: 60 }));
        }

        // The auditor's words, which make it specific to this site.
        if (f.note) {
          children.push(
            para([
              text("Observed:  ", { bold: true, size: 17, color: C.teal }),
              text(f.note, { size: 18 }),
            ], { after: 40 })
          );
        }

        // Conformant entries carry no recommendation: nothing needs fixing.
        if (f.recommendation) {
          children.push(
            para([
              text("Action:  ", { bold: true, size: 17, color: C.teal }),
              text(f.recommendation, { size: 18 }),
            ], { after: 40 })
          );
        }

        const foot = [];
        if (f.legal_reference) foot.push(text(f.legal_reference, { size: 15, color: C.slate }));
        if (f.verification_method) {
          foot.push(text((f.legal_reference ? "    ·    " : "") + f.verification_method,
            { size: 15, color: C.slate, italics: true }));
        }
        if (foot.length) children.push(para(foot, { after: 60 }));

        /*
         * The photographs themselves, under the entry. Elaborate exists to
         * show a finding in full, and a line reading "1 photo" is the one part
         * of that finding a reader cannot see.
         */
        const shots = evidenceRuns(f.photos, images);
        const isText = (f.photos || []).length === 0 ||
          !(images && (f.photos || []).some((u) => images.get(u)));
        if ((f.photos || []).length) {
          children.push(new Paragraph({
            children: shots,
            spacing: { after: isText ? 220 : 200 },
          }));
        } else if (!foot.length) {
          children.push(para("", { after: 180 }));
        }
      });
    });
  });

  return {
    headers: { default: wordHeader(header, assets.logo) },
    footers: { default: wordFooter(header) },
    children,
  };
}

/* ------------------------------------------------------------------ */
/* chrome                                                              */
/* ------------------------------------------------------------------ */

/*
 * The running header, on every page except the cover — the cover section is
 * declared without headers, and carries the wordmark in the artwork already.
 */
const wordHeader = (h, logo) =>
  new Header({
    children: [
      new Paragraph({
        children: [
          ...(logo
            ? [new ImageRun({ data: logo, transformation: HEADER_LOGO_PX }),
               text("   ", { size: 14 })]
            : []),
          text(`${h.organization} — ${h.site}`, { size: 14, color: C.slate }),
        ],
        border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: "DCE3E1", space: 4 } },
      }),
    ],
  });

const wordFooter = (h) =>
  new Footer({
    children: [
      new Paragraph({
        children: [
          text(h.report_id + "    ", { size: 13, color: C.slate }),
          text(issueLine(h) + (issueLine(h) ? "    " : ""), { size: 13, color: C.slate }),
          new TextRun({ children: ["Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES],
            size: 13, color: C.slate, font: FONT }),
        ],
        alignment: AlignmentType.RIGHT,
      }),
    ],
  });

/* ------------------------------------------------------------------ */

/** Build the document. Separated from saving so it can be tested. */
/**
 * The evidence cell: the picture where it could be fetched, the count where it
 * could not.
 *
 * "1 photo" on its own was the worst of the available options — it tells a
 * reader a photograph exists and gives them no way to see it. Worse than the
 * spreadsheet's bare URL, which at least resolves.
 */
function evidenceRuns(photos, images) {
  const list = photos || [];
  if (!list.length) return [new TextRun({ text: "—", size: 15, color: C.slate })];

  const runs = [];
  list.slice(0, 4).forEach((u) => {
    const data = images && images.get(u);
    if (!data) return;
    runs.push(new ImageRun({ data, transformation: { width: 78, height: 59 } }));
  });

  if (!runs.length) {
    return [new TextRun({
      text: `${list.length} photo${list.length === 1 ? "" : "s"}`,
      size: 15, color: C.slate,
    })];
  }
  return runs;
}

export function buildWordDocument(model, images, assets = {}) {
  const { header, sections } = model;
  const byKey = (k) => sections.find((s) => s.key === k);

  const render = (s) => {
    if (s.key === "score_analysis") return scoreAnalysis(s);
    if (s.key === "overall_assessment") return overallAssessment(s);
    if (s.key === "scope") return scope(s);
    return narrative(s);
  };

  const portraitBefore = sections.filter((s) => s.no < 7).flatMap(render);
  const portraitAfter = sections.filter((s) => s.no > 7).flatMap(render);

  return new Document({
    creator: header.prepared_by || "Momentum",
    title: `CSA — ${header.organization} — ${header.site}`,
    description: "Critical Safety Assessment",
    styles: {
      default: {
        document: { run: { font: FONT, size: 19, color: C.ink } },
      },
    },
    sections: [
      { children: cover(model, assets.cover) },
      {
        headers: { default: wordHeader(header, assets.logo) },
        footers: { default: wordFooter(header) },
        children: portraitBefore,
      },
      // The only part of the document the two layouts disagree about.
      model.layout === "elaborate"
        ? elaborateFindings(byKey("findings"), header, images, assets)
        : findingsSection(byKey("findings"), header, images, assets),
      {
        headers: { default: wordHeader(header, assets.logo) },
        footers: { default: wordFooter(header) },
        children: portraitAfter,
      },
    ],
  });
}

export async function exportReportWord(model, opts = {}) {
  // Fetched before the document is built — docx embeds bytes, not URLs.
  // Through the API, which works wherever the app does; S3 is the fallback.
  const images = await fetchPhotos(model, opts.photoBytes);

  /*
   * The cover artwork and the wordmark, as bytes. Same-origin, so none of the
   * trouble S3 gave applies — but docx embeds data rather than URLs, so they
   * still have to be in hand before the document is built.
   */
  const [cover, logo] = await Promise.all([
    assetBytes(coverArt),
    assetBytes(logoArt),
  ]);

  const doc = buildWordDocument(model, images, { cover, logo });
  const blob = await Packer.toBlob(doc);
  const name = opts.fileName || fileNameFor(model, "docx");
  saveAs(blob, name);
  return { name, bytes: blob.size, photos: photoReport(images) };
}
